import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Mail, Phone, MapPin, Send, Linkedin, Instagram, Twitter, Facebook } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [settings, setSettings] = useState({});
  const [socialMedia, setSocialMedia] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const socialIcons = {
    linkedin: Linkedin,
    instagram: Instagram,
    twitter: Twitter,
    facebook: Facebook
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    setTimeout(() => setIsVisible(true), 100);
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [settingsRes, socialRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/cms/settings`),
        axios.get(`${BACKEND_URL}/api/cms/social-media`)
      ]);
      setSettings(settingsRes.data);
      setSocialMedia(socialRes.data);
    } catch (error) {
      console.error('Error fetching contact data:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Mock form submission
    toast({
      title: "Message Sent!",
      description: "We'll get back to you within 24 hours.",
    });
    
    setFormData({
      name: '',
      email: '',
      phone: '',
      message: ''
    });
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-32 bg-gradient-to-br from-[#0A1A50] to-[#060F2E] overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-6 text-center relative z-10">
          <h1
            className="text-6xl md:text-7xl font-light text-white mb-6"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0) scale(1)' : 'translateY(30px) scale(0.95)',
              transition: 'opacity 0.8s ease-out, transform 0.8s ease-out'
            }}
          >
            Get in Touch
          </h1>
          <p
            className="text-white/90 text-xl md:text-2xl font-light max-w-3xl mx-auto"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
              transition: 'opacity 0.8s ease-out 0.2s, transform 0.8s ease-out 0.2s'
            }}
          >
            Let's discuss how we can help transform your real estate vision into reality
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div 
              className="space-y-8"
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateX(0)' : 'translateX(-30px)',
                transition: 'opacity 0.8s ease-out 0.3s, transform 0.8s ease-out 0.3s'
              }}
            >
              <div>
                <div className="h-1 w-20 bg-[#0A1A50] mb-6"></div>
                <h2 className="text-4xl font-light text-[#2d2d2d] mb-6">Contact Information</h2>
                <p className="text-[#B5BBC4] font-light text-lg leading-relaxed">
                  Ready to take the next step? Reach out to us through any of the following channels,
                  and our team will respond promptly.
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start gap-4 group cursor-pointer hover:translate-x-2 transition-transform duration-300">
                  <div className="w-12 h-12 bg-[#0A1A50]/20 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-[#0A1A50] group-hover:scale-110 transition-all duration-300">
                    <Mail className="w-6 h-6 text-[#0A1A50] group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <h3 className="text-lg font-normal text-[#2d2d2d] mb-1">Email</h3>
                    <a
                      href={`mailto:${settings.contact_email || 'info@reagle.ae'}`}
                      className="text-[#B5BBC4] hover:text-[#0A1A50] transition-colors font-light"
                    >
                      {settings.contact_email || 'info@reagle.ae'}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4 group cursor-pointer hover:translate-x-2 transition-transform duration-300">
                  <div className="w-12 h-12 bg-[#0A1A50]/20 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-[#0A1A50] group-hover:scale-110 transition-all duration-300">
                    <Phone className="w-6 h-6 text-[#0A1A50] group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <h3 className="text-lg font-normal text-[#2d2d2d] mb-1">Phone</h3>
                    <a
                      href={`tel:${settings.contact_phone || '+971 4 XXX XXXX'}`}
                      className="text-[#B5BBC4] hover:text-[#0A1A50] transition-colors font-light"
                    >
                      {settings.contact_phone || '+971 4 XXX XXXX'}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4 group cursor-pointer hover:translate-x-2 transition-transform duration-300">
                  <div className="w-12 h-12 bg-[#0A1A50]/20 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-[#0A1A50] group-hover:scale-110 transition-all duration-300">
                    <MapPin className="w-6 h-6 text-[#0A1A50] group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <h3 className="text-lg font-normal text-[#2d2d2d] mb-1">Location</h3>
                    <p className="text-[#B5BBC4] font-light">{settings.contact_address || 'Dubai, United Arab Emirates'}</p>
                  </div>
                </div>
              </div>
              
              {/* Social Media */}
              {socialMedia.length > 0 && (
                <div className="pt-8">
                  <h3 className="text-lg font-normal text-[#2d2d2d] mb-4">Follow Us</h3>
                  <div className="flex gap-4">
                    {socialMedia.map((social) => {
                      const Icon = socialIcons[social.icon];
                      return (
                        <a
                          key={social.id}
                          href={social.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-12 h-12 rounded-full bg-[#0A1A50]/20 flex items-center justify-center hover:bg-[#0A1A50] transition-all duration-300 hover:scale-110 hover:-translate-y-1 group"
                          aria-label={social.name}
                        >
                          {Icon && <Icon className="w-5 h-5 text-[#0A1A50] group-hover:text-white transition-colors" />}
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Contact Form */}
            <div 
              className="bg-gray-50 p-8 rounded-lg shadow-xl hover:shadow-2xl transition-shadow duration-500"
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateX(0)' : 'translateX(30px)',
                transition: 'opacity 0.8s ease-out 0.5s, transform 0.8s ease-out 0.5s'
              }}
            >
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-normal text-[#B5BBC4] mb-2">
                    Full Name *
                  </label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-normal text-[#B5BBC4] mb-2">
                    Email Address *
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-normal text-[#B5BBC4] mb-2">
                    Phone Number
                  </label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full"
                    placeholder="+971 XX XXX XXXX"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-normal text-[#B5BBC4] mb-2">
                    Message *
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full min-h-[150px]"
                    placeholder="Tell us about your project..."
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-[#0A1A50] text-white hover:bg-[#060F2E] py-6 text-lg rounded-lg shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 group"
                >
                  Send Message
                  <Send className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;